import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, from, of, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { getModalConfig, shouldShowAsSheetModal } from 'src/app/shared/utils/modal.utils';

import { WaitingOpponentsData } from '../../../../../core/api/player/schemas/waiting-opponents';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';
import { EventName } from '../../../../../core/services/analytics/event-names';
import { OperatorService } from '../../../../../core/services/operator/operator.service';
import { TiersService } from '../../../../../core/services/tiers/tiers.service';
import { WaitingOpponentsService } from '../../../../../core/services/waiting-opponents/waiting-opponents.service';
import { GameId } from '../../../../../model/game/game-id';
import { ContinueGameDrawerComponent } from '../../../../../ui/drawers/continue-game-drawer/continue-game-drawer.component';
import { WagerTierListComponent } from '../../../../../ui/panels/wager-tier-list/wager-tier-list/wager-tier-list.component';
import { GameControlDisplay } from '../../model/game-control-display';
import { TournamentService } from '../../../../../core/services/tournament/tournament.service';
import { TournamentDataModel } from '../../../../../core/services/tournament/model/tournament-data-model';
import { CountdownPillComponent } from '../../../../../ui/countdown-pill/countdown-pill.component';
import { ButtonComponent } from '../../../../../ui/buttons/button/button.component';
import { TranslocoService } from '@jsverse/transloco';
import { createUnfinishedCountdown } from '../../../../../shared/utils/unfinished-countdown';
import { ThemeService } from '../../../../../core/services/theme/theme.service';
import { GameTutorialService } from '../../../../../core/services/game-tutorial/game-tutorial.service';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import {
  PaymentEventData,
  DuelPaymentEventData,
  TournamentPaymentEventData,
} from 'src/app/shared/types/payment-events';

@Component({
  selector: 'app-tier-first-controls',
  templateUrl: './tier-first-controls.component.html',
  styleUrls: ['./tier-first-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WagerTierListComponent, CountdownPillComponent, ButtonComponent],
})
export class TierFirstControlsComponent
  implements OnInit, OnDestroy, AfterViewInit, GameControlDisplay
{
  @Input({ required: true }) gameId!: GameId;

  @Output() showPaymentEvent = new EventEmitter<PaymentEventData>();
  @Output() launchPracticeEvent = new EventEmitter<void>();
  @Output() launchLocalEvent = new EventEmitter<void>();

  private tiersService = inject(TiersService);
  private waitingOpponentsService = inject(WaitingOpponentsService);
  private roomsResultsService = inject(RoomsResultsService);
  private tournamentService = inject(TournamentService);
  private analyticsService = inject(AnalyticsService);
  private operatorService = inject(OperatorService);
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private translocoService = inject(TranslocoService);
  private themeService = inject(ThemeService);
  private gameTutorialService = inject(GameTutorialService);

  private destroy$ = new Subject<void>();
  private currentTournamentData = signal<TournamentDataModel | undefined>(undefined);

  readonly waitingOpponentsStandard = signal<WaitingOpponentsData | undefined>(undefined);
  readonly allTiersSignal = computed(() => this.tiersService.duelTiers() ?? []);

  readonly unfinishedRoomInitialised = this.roomsResultsService.initialised;
  readonly hasUnfinishedRoom = this.roomsResultsService.hasUnfinishedRoom;

  private initialised$ = toObservable(this.roomsResultsService.initialised);

  readonly unfinishedTimeRemaining = createUnfinishedCountdown(
    this.roomsResultsService,
    this.translocoService,
  );

  readonly showingControls = signal(true);

  ngOnInit(): void {
    this.tournamentService.refreshCurrentTournament();

    this.tournamentService.currentTournamentData$
      .pipe(
        startWith(this.tournamentService.currentTournamentSnapshot),
        switchMap((currentTournamentData) => {
          this.currentTournamentData.set(currentTournamentData);
          if (!this.themeService.hasWaitingOpponents()) {
            return of(undefined);
          }
          return from(
            this.waitingOpponentsService.fetchWaitingOpponents(
              this.gameId,
              currentTournamentData?.tournamentId,
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((waitingOpponents) => {
        this.waitingOpponentsStandard.set(waitingOpponents);
      });

    this.operatorService.updateBalance(OperatorService.IsDemo);
  }

  get currentTournamentDataSignal(): Signal<TournamentDataModel | undefined> {
    return this.currentTournamentData.asReadonly();
  }

  readonly continueButtonLabel = computed(() => this.getTranslation('continue-game'));

  ngAfterViewInit(): void {
    this.initialised$
      .pipe(
        filter((initialised) => initialised),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(async () => {
        if (!this.roomsResultsService.hasUnfinishedRoom()) {
          await this.gameTutorialService.tryShowTutorial(this.gameId);
          return;
        }

        const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
        const continueGameModal = await this.modalController.create({
          component: ContinueGameDrawerComponent,
          ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
        });
        await continueGameModal.present();
        this.analyticsService.log(EventName.ContinueGamePopupShow);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onWagerClick(data: DuelPaymentEventData): void {
    this.analyticsService.log(EventName.SelectTier, { tierId: data.tierData.tierId });
    this.showPaymentEvent.emit(data);
  }

  async onTournamentPlayClick(data: TournamentPaymentEventData): Promise<void> {
    this.analyticsService.log(EventName.TournamentSelectTier, {
      tierId: data.tournamentData.tierId,
      tournamentId: data.tournamentData.tournamentId,
    });
    this.showPaymentEvent.emit(data);
  }

  onPracticeClick(): void {
    this.analyticsService.log(EventName.PracticeButtonClicked);
    this.launchPracticeEvent.emit();
  }

  onLocalClick(): void {
    this.launchLocalEvent.emit();
  }

  async continueGameClicked(): Promise<void> {
    const gameContinued = await this.roomsResultsService.launchUnfinishedGame();
    if (gameContinued) {
      this.analyticsService.log(EventName.ContinueGameScreenLaunchGame);
    }
  }

  private getTranslation(key: string): string {
    return this.translocoService.translate(`components.loading-game-controls.${key}`);
  }
}
